import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Header,
  Button,
  Icon,
  Popup,
  Table,
  Segment,
  Loader,
  Label,
  Grid,
  Pagination,
  Container,
  Message,
} from "semantic-ui-react";

import { PAGE_SIZE, formatDate } from "../../constants";
import { useGetPartnerEvaluatedSessionsQuery } from "../evaluation_sessions/evaluationSessionApiSlice";
import PartnerEvaluationTabbedResult from "./PartnerEvaluationTabbedResult";
import "./EvaluationStyle.css";
import "../../components/SharedCard.css";

const PartnerEvaluatedSessionList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page");
  const page_size = queryParams.get("page_size");

  // get query params frontend urlpath and remove null ones to pass to backend
  let qp = { page, page_size };
  qp = Object.entries(qp)
    .filter((e) => e[1])
    .reduce((accumulator, [k, v]) => {
      accumulator[k] = v;
      return accumulator;
    }, {});

  // GET list
  const { data, isLoading, isSuccess, isError, error } = useGetPartnerEvaluatedSessionsQuery({ ...qp });

  // GET by ID and make it accessible by variable evaluation_session_detail
  const [selectedPartnerEvaluatedSession, setSelectedPartnerEvaluatedSession] = useState(null);
  const handleSelectPartnerEvaluatedSession = (evaluation_session) => {
    setSelectedPartnerEvaluatedSession(evaluation_session);
  };

  const handlePaginationChange = (e, { activePage }) => {
    setSelectedPartnerEvaluatedSession(null);
    navigate(`/partnerevaluatedsessionlist?page=${activePage}&page_size=${PAGE_SIZE}`);
  };

  if (isLoading) return <Loader active />;
  if (isError) return null;

  let content;
  if (isSuccess) {
    content = (
      <div
        style={{
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header Section */}
        <div
          style={{
            flex: '0 0 auto',
            padding: '16px 0',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0,0,0,0.05)',
          }}
        >
          <Container fluid>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '0 20px',
              }}
            >
              <div
                style={{
                  width: '40px',
                  flexShrink: 0, // stays a circle when the text beside it wraps
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#F2F2F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  name="file alternate outline"
                  style={{ margin: 0, color: '#007AFF', fontSize: '18px' }}
                />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    margin: 0,
                    color: '#1d1d1f',
                  }}
                >
                  수동 채점 롤플레이 목록
                </h2>
                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                  View partner evaluation results and comparison
                </p>
              </div>
            </div>
          </Container>
        </div>

        <Container
          fluid
          style={{
            padding: '20px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Grid style={{ margin: 0 }} container={false}>
            {/* Left Pane: Table */}
            <Grid.Column width={8} style={{ maxHeight: '100%', paddingBottom: 0 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflowY: 'hidden', // Changed from auto to hidden for the main container
                  height: 'calc(100vh - 200px)', // Matched height with ChatSessionsList
                }}
              >
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <Table
                    compact="very"

                    selectable
                    style={{ margin: 0, border: 'none' }}
                  >
                    <Table.Header style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
                      <Table.Row>
                        <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>ID</Table.HeaderCell>
                        <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>롤플레이 일시</Table.HeaderCell>
                        <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>
                          <Icon name="chat" color="grey" />
                          고객 페르소나
                        </Table.HeaderCell>
                        <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>
                          <Icon name="chat" color="blue" />
                          응대 직원
                        </Table.HeaderCell>
                        <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>
                          <Icon name="building outline" color="blue" />
                          소속
                        </Table.HeaderCell>

                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {data.results.map((partner_evaluation_session, i) => (
                        <Table.Row
                          key={partner_evaluation_session.id}
                          active={
                            selectedPartnerEvaluatedSession &&
                            selectedPartnerEvaluatedSession.id === partner_evaluation_session.id
                          }
                          onClick={() => handleSelectPartnerEvaluatedSession(partner_evaluation_session)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Table.Cell textAlign="center">
                            <Label circular>
                              <Icon name="file alternate outline" />
                              {partner_evaluation_session.id.slice(0, 5)}
                            </Label>
                          </Table.Cell>

                          <Table.Cell>
                            {formatDate(partner_evaluation_session.created_at)}
                          </Table.Cell>

                          <Table.Cell>
                            {partner_evaluation_session.chat_session.persona?
                              <Popup
                                header={partner_evaluation_session.chat_session.persona.name}
                                content={partner_evaluation_session.chat_session.persona.purpose_of_visit}
                                trigger={<span>{partner_evaluation_session.chat_session.persona.name}</span>}
                              />
                              : <span style={{ color: 'grey' }}>실제 고객</span>
                            }
                          </Table.Cell>

                          <Table.Cell>
                            {partner_evaluation_session.chat_session.trainee.username}
                          </Table.Cell>

                          <Table.Cell>
                            {partner_evaluation_session.chat_session.trainee.contractor ? 
                              partner_evaluation_session.chat_session.trainee.contractor.name : 
                              <span style={{ color: 'grey' }}>-</span>
                            }
                          </Table.Cell>



                        
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
                
                {/* Pagination inside Left Pane */}
                <div style={{ 
                  padding: '12px', 
                  borderTop: '1px solid rgba(0,0,0,0.05)', 
                  display: 'flex', 
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa'
                }}>
                  <Pagination
                    activePage={page}
                    onPageChange={handlePaginationChange}
                    totalPages={Math.ceil(data.count / page_size)}
                    size="mini"
                    className="custom-pagination"
                    ellipsisItem={{ content: <Icon name='ellipsis horizontal' />, icon: true }}
                    firstItem={{ content: <Icon name='angle double left' />, icon: true }}
                    lastItem={{ content: <Icon name='angle double right' />, icon: true }}
                    prevItem={{ content: <Icon name='angle left' />, icon: true }}
                    nextItem={{ content: <Icon name='angle right' />, icon: true }}
                  />
                </div>
              </div>
            </Grid.Column>

            <Grid.Column width={8} style={{ height: 'calc(100vh - 200px)', paddingBottom: 0 }}>
              <div 
                style={{
                  height: '100%',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {selectedPartnerEvaluatedSession ? (
                  <PartnerEvaluationTabbedResult
                    id={selectedPartnerEvaluatedSession.id}
                    key={selectedPartnerEvaluatedSession.id}
                    partner_session_id={selectedPartnerEvaluatedSession.id}
                    evaluation_session_id={selectedPartnerEvaluatedSession.evaluation_session.id}
                  />
                ) : (
                  <div style={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#86868b'
                  }}>
                    <Icon name="file alternate outline" size="huge" style={{ marginBottom: '20px', color: '#e5e5ea' }} />
                    <p>Select a session to view comparison results</p>
                  </div>
                )}
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </div>
    );
  }
  return content;
}

export default PartnerEvaluatedSessionList;
